import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { getDefaultSiteLanguage } from '../lib/maison-adji-api';

export type Lang = 'fr' | 'en';
export type DetectionMode = 'auto' | 'manual';

// ── Traductions UI intégrées (fallback si Supabase indisponible) ──
const TRANSLATIONS: Record<string, Record<Lang, string>> = {
  'nav.shop':            { fr: 'Boutique',                           en: 'Shop' },
  'nav.studio':          { fr: 'Prêt à porter',                       en: 'Ready to wear' },
  'nav.bespoke':         { fr: 'Bespoke',                             en: 'Bespoke' },
  'nav.custom':          { fr: 'Sur-mesure',                          en: 'Bespoke' },
  'nav.the_brand':       { fr: 'La Marque',                           en: 'The Brand' },
  'nav.all_collections': { fr: 'Toutes les collections',              en: 'All Collections' },
  'nav.login':           { fr: 'Connexion',                           en: 'Sign in' },
  'nav.cart':            { fr: 'Panier',                              en: 'Cart' },
  'nav.about':           { fr: 'À propos',                            en: 'About' },
  'nav.documentary':     { fr: 'Documentaire',                        en: 'Documentary' },
  'nav.press':           { fr: 'Presse',                              en: 'Press' },
  'home.discover':       { fr: 'Découvrir la collection',             en: 'Discover the collection' },
  'home.tagline':        { fr: "L'élégance réinventée",                en: 'Elegance reinvented' },
  'home.subtitle':       { fr: 'Haute couture',                       en: 'Haute couture' },
  'product.size':        { fr: 'Taille',                              en: 'Size' },
  'product.color':       { fr: 'Couleur',                             en: 'Color' },
  'product.fabric':      { fr: 'Tissu',                               en: 'Fabric' },
  'product.add_to_cart': { fr: 'Ajouter au panier',                   en: 'Add to cart' },
  'product.buy_now':     { fr: 'Acheter maintenant',                  en: 'Buy now' },
  'product.sold_out':    { fr: 'Épuisé',                              en: 'Sold out' },
  'product.reviews':     { fr: 'Avis',                                en: 'Reviews' },
  'product.similar':     { fr: 'Vous aimerez aussi',                  en: 'You may also like' },
  'cart.empty':          { fr: 'Votre panier est vide.',               en: 'Your cart is empty.' },
  'cart.title':          { fr: 'Mon panier',                          en: 'My cart' },
  'cart.total':          { fr: 'Total',                               en: 'Total' },
  'cart.taxes':          { fr: 'Taxes',                               en: 'Taxes' },
  'cart.shipping':       { fr: 'Livraison',                           en: 'Shipping' },
  'cart.shipping_calc':  { fr: 'Calculé à la caisse',                 en: 'Calculated at checkout' },
  'cart.subtotal':       { fr: 'Sous-total',                          en: 'Subtotal' },
  'cart.remove_item':    { fr: 'Retirer du panier',                   en: 'Remove from cart' },
  'cart.decrease':       { fr: 'Diminuer',                             en: 'Decrease' },
  'cart.increase':       { fr: 'Augmenter',                           en: 'Increase' },
  'cart.checkout':       { fr: 'Commander',                           en: 'Checkout' },
  'cart.checkout_btn':   { fr: 'Passer à la caisse',                  en: 'Checkout' },
  'cart.checkout_with':  { fr: 'Passer à la caisse avec',              en: 'Checkout with' },
  'cart.checkout_multi': { fr: 'Passer à la caisse (plusieurs boutiques)', en: 'Checkout (multiple shops)' },
  'cart.checkout_guest_modal_title':   { fr: 'Suivre votre commande',                    en: 'Track your order' },
  'cart.checkout_guest_modal_message': { fr: 'Connectez-vous pour suivre votre commande, ou continuez en invité.', en: 'Sign in to track your order, or continue as guest.' },
  'cart.checkout_guest_continue':     { fr: 'Continuer sans se connecter',               en: 'Continue without signing in' },
  'cart.checkout_guest_cancel':       { fr: 'Annuler',                                   en: 'Cancel' },
  'checkout.success_title':          { fr: 'Paiement réussi',                            en: 'Payment successful' },
  'checkout.success_message':        { fr: 'Merci pour votre commande. Nous avons bien reçu votre paiement.', en: 'Thank you for your order. We have received your payment.' },
  'checkout.success_continue':       { fr: 'Continuer les achats',                       en: 'Continue shopping' },
  'checkout.success_track_title':    { fr: 'Suivre votre commande',                      en: 'Track your order' },
  'checkout.success_track_message':  { fr: 'Créez un compte ou connectez-vous pour suivre votre commande et vos prochains achats.', en: 'Create an account or sign in to track this order and your future purchases.' },
  'checkout.success_track_cta':      { fr: 'Créer un compte / Se connecter',             en: 'Create account / Sign in' },
  'checkout.failed_title':           { fr: 'Paiement annulé ou échoué',                  en: 'Payment cancelled or failed' },
  'checkout.failed_message':         { fr: 'Votre paiement n’a pas abouti. Vous pouvez réessayer ou modifier votre panier.', en: 'Your payment did not go through. You can try again or update your cart.' },
  'checkout.failed_home':            { fr: 'Retour à l’accueil',                         en: 'Back to home' },
  'checkout.failed_cart':            { fr: 'Voir mon panier',                             en: 'View my cart' },
  'account.my_orders':   { fr: 'Mes commandes',                       en: 'My orders' },
  'account.my_address':  { fr: 'Mon adresse',                         en: 'My address' },
  'account.my_profile':  { fr: 'Mon profil',                          en: 'My profile' },
  'account.business':    { fr: 'Espace Business',                     en: 'Business space' },
  'account.sign_out':    { fr: 'Se déconnecter',                     en: 'Sign out' },
  'account.user':        { fr: 'Utilisateur',                         en: 'User' },
  'account.orders_empty': { fr: 'Aucune commande pour le moment.',     en: 'No orders yet.' },
  'account.order_number': { fr: 'Commande',                           en: 'Order' },
  'account.order_date':  { fr: 'Date',                                en: 'Date' },
  'account.order_status': { fr: 'Statut',                             en: 'Status' },
  'account.order_total':  { fr: 'Total',                               en: 'Total' },
  'account.profile_photo': { fr: 'Photo de profil',                   en: 'Profile photo' },
  'account.add_photo':    { fr: 'Ajouter une photo',                 en: 'Add photo' },
  'account.change_photo': { fr: 'Modifier la photo',                 en: 'Change photo' },
  'account.first_name':  { fr: 'Prénom',                               en: 'First name' },
  'account.last_name':   { fr: 'Nom',                                 en: 'Last name' },
  'account.phone':      { fr: 'Téléphone',                            en: 'Phone' },
  'account.save':       { fr: 'Enregistrer',                           en: 'Save' },
  'account.saved':      { fr: 'Profil enregistré.',                   en: 'Profile saved.' },
  'account.addresses':  { fr: 'Adresses de livraison',                en: 'Shipping addresses' },
  'account.add_address': { fr: 'Ajouter une adresse',                 en: 'Add address' },
  'account.no_address':  { fr: 'Aucune adresse enregistrée.',         en: 'No addresses saved.' },
  'account.add_address_anytime': { fr: 'Vous pouvez ajouter une adresse à tout moment.', en: 'You can add an address anytime.' },
  'account.full_name':   { fr: 'Nom complet',                         en: 'Full name' },
  'account.city':       { fr: 'Ville',                                en: 'City' },
  'account.country':    { fr: 'Pays',                                 en: 'Country' },
  'account.address_line1': { fr: 'Adresse',                           en: 'Address' },
  'account.address_line2': { fr: 'Complément',                        en: 'Address line 2' },
  'account.postal_code': { fr: 'Code postal',                         en: 'Postal code' },
  'account.region':     { fr: 'Région',                               en: 'Region' },
  'account.default':    { fr: 'Par défaut',                            en: 'Default' },
  'account.set_default': { fr: 'Définir par défaut',                  en: 'Set as default' },
  'common.cancel':      { fr: 'Annuler',                               en: 'Cancel' },
  'business.dashboard':  { fr: 'Tableau de bord',                      en: 'Dashboard' },
  'business.orders':    { fr: 'Commandes',                            en: 'Orders' },
  'business.products':  { fr: 'Produits',                             en: 'Products' },
  'business.notifications': { fr: 'Notifications',                      en: 'Notifications' },
  'business.settings':  { fr: 'Paramètres',                           en: 'Settings' },
  'business.orders_in_progress': { fr: 'Commandes en cours',          en: 'Orders in progress' },
  'business.access_denied': { fr: 'Accès réservé à l’administrateur.', en: 'Access restricted to administrator.' },
  'nav.sign_in':         { fr: 'Se connecter',                        en: 'Sign in' },
  'footer.rights':       { fr: 'Tous droits réservés',                en: 'All rights reserved' },
  'footer.couture':      { fr: 'À Propos de nous',                   en: 'About us' },
  'footer.studio':       { fr: 'Prêt à porter',                       en: 'Ready to wear' },
  'footer.bespoke':      { fr: 'Bespoke',                             en: 'Bespoke' },
  'footer.contact':      { fr: 'Contact',                             en: 'Contact' },
  'footer.legal':        { fr: 'Mentions légales',                    en: 'Legal' },
  'footer.shipping':     { fr: 'Livraison & Retours',                 en: 'Shipping & Returns' },
  'lang.fr':             { fr: 'Français',                            en: 'French' },
  'lang.en':             { fr: 'Anglais',                             en: 'English' },
  'lang.auto':           { fr: 'Auto',                                en: 'Auto' },
  'login.title':         { fr: 'Connexion',                          en: 'Sign in' },
  'login.subtitle':      { fr: 'Accédez à votre compte Maison Adji', en: 'Access your Maison Adji account' },
  'login.email':         { fr: 'Email',                               en: 'Email' },
  'login.phone':         { fr: 'Téléphone',                           en: 'Phone' },
  'login.others':        { fr: 'Autres',                              en: 'Others' },
  'login.placeholder_email':    { fr: 'votre@email.com',              en: 'your@email.com' },
  'login.placeholder_password': { fr: 'Mot de passe',                 en: 'Password' },
  'login.submit':        { fr: 'Se connecter',                        en: 'Sign in' },
  'login.forgot':        { fr: 'Mot de passe oublié ?',               en: 'Forgot password?' },
  'login.send_code':     { fr: 'Envoyer le code',                     en: 'Send code' },
  'login.verify':        { fr: 'Vérifier le code',                    en: 'Verify code' },
  'login.resend':        { fr: 'Renvoyer le code',                    en: 'Resend code' },
  'login.otp_prompt':    { fr: 'Entrez le code de vérification à 6 chiffres', en: 'Enter the 6-digit verification code' },
  'login.verifying':     { fr: 'Vérification en cours...',            en: 'Verifying...' },
  'login.sending':       { fr: 'Envoi en cours...',                    en: 'Sending...' },
  'login.loading':       { fr: 'Connexion en cours...',               en: 'Signing in...' },
  'login.continue_whatsapp': { fr: 'Continuer avec WhatsApp',        en: 'Continue with WhatsApp' },
  'login.continue_google':   { fr: 'Continuer avec Google',          en: 'Continue with Google' },
  'login.whatsapp_code': { fr: 'Code WhatsApp',                       en: 'WhatsApp code' },
  'login.signup_title':      { fr: 'Créer un compte',                 en: 'Create an account' },
  'login.signup_submit':     { fr: 'S\'inscrire',                     en: 'Sign up' },
  'login.signup_loading':    { fr: 'Inscription en cours...',        en: 'Signing up...' },
  'login.switch_to_signup':  { fr: 'Créer un compte',                en: 'Create an account' },
  'login.switch_to_login':   { fr: 'Déjà un compte ? Se connecter',   en: 'Already have an account? Sign in' },
  'login.check_email':       { fr: 'Vérifiez votre boîte mail pour confirmer votre compte.', en: 'Check your email to confirm your account.' },
  'contact.title':          { fr: 'Contact',                           en: 'Contact' },
  'contact.subtitle':       { fr: 'Une question, une demande sur-mesure ou une collaboration ? Écrivez-nous.', en: 'A question, a bespoke request or a collaboration? Get in touch.' },
  'contact.email_label':     { fr: 'Notre adresse',                    en: 'Our email' },
  'contact.name':           { fr: 'Nom',                               en: 'Name' },
  'contact.email':          { fr: 'Email',                             en: 'Email' },
  'contact.subject':        { fr: 'Sujet',                             en: 'Subject' },
  'contact.message':        { fr: 'Message',                          en: 'Message' },
  'contact.placeholder_name':   { fr: 'Votre nom',                    en: 'Your name' },
  'contact.placeholder_email':  { fr: 'votre@email.com',               en: 'your@email.com' },
  'contact.placeholder_subject': { fr: 'Objet de votre message',     en: 'Subject of your message' },
  'contact.placeholder_message': { fr: 'Votre message...',           en: 'Your message...' },
  'contact.send':           { fr: 'Envoyer',                          en: 'Send' },
  'contact.sending':        { fr: 'Envoi en cours...',                 en: 'Sending...' },
  'contact.sent':           { fr: 'Message envoyé',                   en: 'Message sent' },
  'contact.sent_help':      { fr: 'Nous vous répondrons à l’adresse indiquée.', en: 'We will reply to the email you provided.' },
  'collections.title':      { fr: 'Collections',   en: 'Collections' },
  'collections.all':        { fr: 'Tout',         en: 'All' },
  'collections.accessoires': { fr: 'Accessoires',  en: 'Accessories' },
  'collections.bespoke':    { fr: 'Bespoke',      en: 'Bespoke' },
  'collections.studio':     { fr: 'Prêt à porter', en: 'Ready to wear' },
  'sort.title':             { fr: 'Trier par',   en: 'Sort by' },
  'about.title':            { fr: 'À propos de la marque',              en: 'About the brand' },
  'about.subtitle':         { fr: 'Notre histoire et notre vision.',     en: 'Our story and our vision.' },
  'about.section1.lead':     { fr: 'Maison Adji est née d’une histoire personnelle, d’un regard posé entre deux cultures et d’un amour profond pour l’élégance.', en: 'Maison Adji was born from a personal story, a gaze between two cultures, and a deep love for elegance.' },
  'about.section1.p1':      { fr: 'Fondée par Adji Touré, sénégalaise installée en France, la marque est l’expression d’un parcours singulier : celui d’une créatrice qui évolue entre différentes influences culturelles, esthétiques et artisanales. Très tôt, la mode est devenue pour elle un langage, une manière d’exprimer son identité, sa sensibilité et sa vision du style.', en: 'Founded by Adji Touré, a Senegalese designer based in France, the brand is the expression of a unique journey: that of a creator moving between different cultural, aesthetic and artisanal influences. From an early age, fashion became her language, a way to express her identity, sensibility and vision of style.' },
  'about.section1.p2':      { fr: 'Maison Adji propose des créations pour celles et ceux qui recherchent une élégance authentique, intemporelle et affirmée. La marque se situe à la croisée des inspirations africaines et européennes, où les héritages culturels rencontrent une approche contemporaine du vêtement.', en: 'Maison Adji offers creations for those seeking authentic, timeless and confident elegance. The brand stands at the crossroads of African and European inspirations, where cultural heritage meets a contemporary approach to clothing.' },
  'about.section2.title':   { fr: 'Savoir-faire & matières',              en: 'Craftsmanship & materials' },
  'about.section2.p1':      { fr: 'Chaque pièce est entièrement conçue et réalisée en France. Maison Adji revendique un savoir-faire artisanal et une production à taille humaine, où chaque vêtement est fabriqué à la main avec une attention particulière portée aux détails, aux lignes et à la qualité des finitions.', en: 'Each piece is entirely designed and made in France. Maison Adji champions artisanal know-how and human-scale production, where every garment is handcrafted with particular attention to details, lines and the quality of finishes.' },
  'about.section2.p2':      { fr: 'Fidèle à ses racines, la marque sélectionne également ses matières premières sur le continent africain, privilégiant des tissus nobles et des textiles riches de leur histoire. Cette rencontre entre artisanat, matières d’exception et création contemporaine donne naissance à des pièces uniques qui racontent une histoire.', en: 'True to its roots, the brand also sources its raw materials on the African continent, favouring noble fabrics and textiles rich in history. This meeting of craftsmanship, exceptional materials and contemporary creation gives birth to unique pieces that tell a story.' },
  'about.quote':            { fr: 'Maison Adji est plus qu’une marque. C’est la rencontre entre deux continents, un hommage au savoir-faire artisanal et une vision du style libre, élégante et intemporelle.', en: 'Maison Adji is more than a brand. It is the meeting of two continents, a tribute to artisanal know-how and a vision of style that is free, elegant and timeless.' },
};

// ── Détection de la langue par IP (via ipapi.co) ──
async function detectLanguageByIP(): Promise<Lang> {
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
    if (!res.ok) throw new Error();
    const data = await res.json();
    // Pays francophones : SN, FR, BE, CH, CI, CM, CD, ML, BF, NE, TG, BJ, GN, GA, CG, MG, DZ, MA, TN…
    const frenchCountries = [
      'SN','FR','BE','CH','CI','CM','CD','ML','BF','NE','TG','BJ','GN','GA','CG','MG',
      'DZ','MA','TN','LU','MC','GF','GP','MQ','RE','YT','PM','WF','PF','NC','MF',
    ];
    return frenchCountries.includes(data.country_code) ? 'fr' : 'en';
  } catch {
    // Fallback : langue du navigateur
    return navigator.language.startsWith('fr') ? 'fr' : 'en';
  }
}

// ── Context ──
interface LanguageContextType {
  lang: Lang;
  mode: DetectionMode;
  setLang: (lang: Lang) => void;
  setMode: (mode: DetectionMode) => void;
  t: (key: string) => string;
  /** Retourne le champ localisé d'un objet : obj.name_fr / obj.name_en ou obj.name */
  localize: <T extends Record<string, any>>(obj: T, field: string) => string;
  /** Recharge la langue par défaut du site (après sauvegarde dans Paramètres du site) */
  refreshDefaultLanguage: () => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const LS_LANG = 'ma_lang';
const LS_MODE = 'ma_lang_mode';

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>('fr');
  const [mode, setModeState] = useState<DetectionMode>('auto');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const savedMode = (localStorage.getItem(LS_MODE) as DetectionMode) ?? 'auto';
    const savedLang = (localStorage.getItem(LS_LANG) as Lang) ?? null;

    setModeState(savedMode);

    if (savedMode === 'manual' && savedLang) {
      setLangState(savedLang);
      setReady(true);
    } else {
      // Mode auto : langue par défaut du site (Paramètres du site), sinon détection IP
      getDefaultSiteLanguage()
        .then((siteLang) => {
          setLangState(siteLang);
          setReady(true);
        })
        .catch(() => {
          detectLanguageByIP().then((detected) => {
            setLangState(detected);
            setReady(true);
          });
        });
    }
  }, []);

  const refreshDefaultLanguage = useCallback(async () => {
    const siteLang = await getDefaultSiteLanguage().catch(() => 'fr' as const);
    setLangState(siteLang);
  }, []);

  const setLang = (l: Lang) => {
    setLangState(l);
    setModeState('manual');
    localStorage.setItem(LS_LANG, l);
    localStorage.setItem(LS_MODE, 'manual');
  };

  const setMode = (m: DetectionMode) => {
    setModeState(m);
    localStorage.setItem(LS_MODE, m);
    if (m === 'auto') {
      localStorage.removeItem(LS_LANG);
      detectLanguageByIP().then(setLangState);
    }
  };

  const t = (key: string): string => {
    return TRANSLATIONS[key]?.[lang] ?? TRANSLATIONS[key]?.['fr'] ?? key;
  };

  // Retourne le champ localisé : obj.description_en ou obj.description (fr)
  const localize = <T extends Record<string, any>>(obj: T, field: string): string => {
    if (lang === 'en') {
      const enField = `${field}_en`;
      if (obj[enField]) return obj[enField];
    }
    return obj[field] ?? '';
  };

  if (!ready) return null;

  return (
    <LanguageContext.Provider value={{ lang, mode, setLang, setMode, t, localize, refreshDefaultLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
}
